import styles from "./PrivacyPolicy.module.scss";

export function TermsOfUseEN() {
  return (
    <div className={styles.policy}>
      <h3>Terms of Service</h3>
      <p>
        <strong>Last Updated: October 8, 2026</strong>
      </p>

      <p>
        These Terms of Use ("Terms"), together with any Order Form and any other documents incorporated by reference
        herein (collectively, the "Agreement") form a binding legal agreement between Palamar Teknoloji Anonim Şirketi ("Palmate",
        "Company", "we", "us", or "our") and the entity or individual using our Services ("Customer", "you", or "your").
        These Terms govern your access to and use of our AI-powered chatbot development services and platform. By using
        our Services or executing an Order Form, you agree to be bound by these Terms.
      </p>

      <h3>1. SERVICES AND PLATFORM</h3>

      <h4>1.1 Platform Access</h4>
      <ul>
        <li>Our AI-powered chatbot development platform ("Platform")</li>
        <li>Associated APIs and development tools ("APIs")</li>
        <li>Professional services as specified in any Order Form or Statement of Work</li>
      </ul>

      <h4>1.2 Service Updates</h4>
      <p>
        We reserve the right to make changes and updates to our Services, provided such changes do not materially
        degrade the functionality. We will notify you of material changes via email or platform notification.
      </p>

      <h4>1.3 Your Responsibilities</h4>
      <ul>
        <li>Use the Platform only in accordance with this Agreement and applicable laws</li>
        <li>Be responsible for all activities under your account</li>
        <li>Not transfer, resell, or make the Platform available to unauthorized third parties</li>
        <li>Implement appropriate security measures to prevent unauthorized access</li>
        <li>Ensure your chatbots comply with applicable laws and regulations</li>
        <li>Obtain necessary consents from end users who interact with your chatbots</li>
      </ul>

      <h4>1.4 Acceptable Use</h4>
      <ul>
        <li>Violate any applicable laws or regulations</li>
        <li>Infringe on intellectual property rights</li>
        <li>Create harmful, offensive, or misleading content</li>
        <li>Engage in fraudulent or deceptive practices</li>
        <li>Distribute malware or other harmful code</li>
        <li>Interfere with the Platform's operation</li>
      </ul>

      <h4>1.5 SMS Terms</h4>
      <p>
        Palmate, operated by Palamar Teknoloji Anonim Şirketi, may send transactional SMS messages for account
        authentication, one-time passcodes (OTPs), identity verification, and security-related purposes. Messages are
        sent only after the user provides a mobile phone number and initiates or requests a verification action.
      </p>
      <ul>
        <li>Message frequency varies based on user authentication activity.</li>
        <li>Message and data rates may apply.</li>
        <li>Reply STOP to stop receiving SMS messages and HELP for help.</li>
        <li>For support, contact support@palmate.ai.</li>
        <li>Carriers are not liable for delayed or undelivered messages.</li>
      </ul>
      <p>
        Our handling of mobile phone numbers and SMS opt-in data is described in our Privacy Policy. SMS consent is not
        shared with third parties or affiliates for their own marketing or promotional purposes.
      </p>

      <h3>2. INTELLECTUAL PROPERTY AND DATA</h3>

      <h4>2.1 Your Data</h4>
      <p>
        You retain ownership of all data you provide to our Platform ("Customer Data"). You grant us a limited license
        to process your Customer Data solely to provide our Services and improve our Platform through aggregated,
        de-identified analytics.
      </p>

      <p>
        For enterprise customers, the license above is limited to delivering the agreed services in accordance with
        the customer's instructions and the applicable service agreement and Data Processing Addendum. Palmate does
        not use enterprise customer data, including conversations, uploaded documents and knowledge-base content, to
        train, fine-tune or improve AI models. This restriction also applies to aggregated or de-identified versions
        of enterprise customer data.
      </p>

      <h4>2.2 Our Intellectual Property</h4>
      <ul>
        <li>The Platform and associated technology</li>
        <li>Our proprietary AI models and algorithms</li>
        <li>Any improvements or enhancements we develop</li>
        <li>Aggregated and anonymized usage data</li>
      </ul>

      <h4>2.3 Generated Content</h4>
      <p>
        Content generated by our AI models ("Outputs") is provided to you for your use. However, we cannot guarantee
        that Outputs will be unique, accurate, or free from errors or bias.
      </p>

      <h3>3. AI GOVERNANCE AND LIMITATIONS</h3>

      <h4>3.1 AI Technology Acknowledgment</h4>
      <ul>
        <li>Our Services use artificial intelligence and machine learning technologies</li>
        <li>AI-generated content may contain errors, biases, or inaccuracies</li>
        <li>Outputs should be reviewed and validated before use</li>
        <li>AI systems have inherent limitations and may produce unexpected results</li>
      </ul>

      <h4>3.2 Human Oversight</h4>
      <p>
        You agree to maintain appropriate human oversight of AI-generated content and not rely solely on automated
        systems for critical decisions.
      </p>

      <h4>3.3 Prohibited AI Uses</h4>
      <ul>
        <li>Make decisions about creditworthiness, employment, or insurance</li>
        <li>Generate content intended to deceive or mislead</li>
        <li>Create deepfakes or other synthetic media without proper disclosure</li>
        <li>Engage in surveillance or tracking without consent</li>
      </ul>

      <h3>4. FEES AND PAYMENT</h3>

      <h4>4.1 Subscription Fees</h4>
      <p>
        Fees, currency, any fixed package or setup charges, and the purchased service scope are specified in the
        Order Form or service agreement accepted by the parties.
      </p>

      <h4>4.2 Usage-Based Charges</h4>
      <p>Usage charges are calculated at the end of the current month from actual usage, using the agreed unit prices and conditions.</p>

      <h4>4.3 Payment Terms</h4>
      <p>Payment takes place at month-end after usage. Invoicing, payment and the consequences of late payment are governed by the agreement between the parties and applicable law.</p>

      <h4>4.4 Taxes</h4>
      <p>
        Whether VAT is included is stated with the relevant price or offer. The total amount and applicable taxes
        are disclosed before payment; the accepted Order Form or service agreement determines the applicable charges.
      </p>

      <h4>4.5 Refunds</h4>
      <p>Unused message allowances are non-refundable. Billing or collection errors are reviewed separately. Other refund requests are assessed under the contract and applicable law; mandatory statutory rights are reserved.</p>

      <h3>5. CONFIDENTIALITY</h3>
      <p>
        Each party agrees to protect the other party's confidential information and use it solely for purposes related
        to this Agreement. Confidential information excludes publicly available information or information independently
        developed.
      </p>

      <h3>6. WARRANTIES AND DISCLAIMERS</h3>

      <h4>6.1 Our Warranties</h4>
      <p>
        We warrant that our Services will perform substantially in accordance with our published specifications and
        industry standards.
      </p>

      <h4>6.2 Your Warranties</h4>
      <ul>
        <li>You have the right to use all Customer Data provided to us</li>
        <li>Your use of our Services will comply with applicable laws</li>
        <li>You will obtain necessary consents from end users</li>
      </ul>

      <h4>6.3 DISCLAIMER</h4>
      <p>
        EXCEPT AS EXPRESSLY STATED, OUR SERVICES ARE PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DISCLAIM ALL
        IMPLIED WARRANTIES INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
      </p>

      <h4>6.4 AI LIMITATIONS DISCLAIMER</h4>
      <p>
        AI-GENERATED CONTENT MAY CONTAIN ERRORS, BIASES, OR INACCURACIES. WE DO NOT WARRANT THE ACCURACY, COMPLETENESS,
        OR RELIABILITY OF ANY AI-GENERATED CONTENT. YOU ARE RESPONSIBLE FOR REVIEWING AND VALIDATING ALL OUTPUTS.
      </p>

      <h3>7. INDEMNIFICATION</h3>

      <h4>7.1 Our Indemnification</h4>
      <p>
        We will defend you against third-party claims that our Services infringe valid intellectual property rights,
        subject to your prompt notification and cooperation.
      </p>

      <h4>7.2 Your Indemnification</h4>
      <ul>
        <li>Your violation of this Agreement</li>
        <li>Your Customer Data or use thereof</li>
        <li>Your chatbots or end user interactions</li>
        <li>Your violation of applicable laws</li>
      </ul>

      <h3>8. LIMITATION OF LIABILITY</h3>

      <h4>8.1 Liability Cap</h4>
      <p>
        Each party's total liability for all claims under this Agreement will not exceed the fees paid by you in the 12
        months preceding the claim.
      </p>

      <h4>8.2 Excluded Damages</h4>
      <p>
        Neither party will be liable for indirect, consequential, punitive, or special damages, including lost profits
        or business opportunities.
      </p>

      <h4>8.3 Exceptions</h4>
      <p>
        These limitations do not apply to indemnification obligations, confidentiality breaches, or gross negligence.
      </p>

      <h3>9. TERM AND TERMINATION</h3>

      <h4>9.1 Term</h4>
      <p>
        Service begins when the user accesses the system after activation is complete. The service duration,
        commitment, notice and termination periods are those specified in the accepted Order Form or service agreement.
      </p>

      <h4>9.2 Termination Rights</h4>
      <p>Cancellation and termination rights, including any notice or remedy periods, are governed by the agreement between the parties and applicable law. A cancellation request does not automatically remove contractual payment obligations.</p>

      <h4>9.3 Effect of Termination</h4>
      <ul>
        <li>Your access to the Platform will cease</li>
        <li>You must pay all outstanding fees</li>
        <li>We will retain your data for a reasonable period for export</li>
        <li>Confidentiality obligations will survive</li>
      </ul>

      <h3>10. GENERAL PROVISIONS</h3>

      <h4>10.1 Governing Law</h4>
      <p>This Agreement is governed by the laws of the Republic of Türkiye, excluding conflict of law principles.</p>

      <h4>10.2 Dispute Resolution</h4>
      <p>
        Any disputes will be resolved through binding arbitration in Istanbul, Turkey, except for claims involving
        intellectual property rights.
      </p>

      <h4>10.3 Force Majeure</h4>
      <p>Neither party will be liable for delays or failures due to circumstances beyond their reasonable control.</p>

      <h4>10.4 Assignment</h4>
      <p>
        You may not assign this Agreement without our written consent. We may assign this Agreement in connection with a
        merger, acquisition, or asset sale.
      </p>

      <h4>10.5 Entire Agreement</h4>
      <p>
        These Terms are read together with the accepted Order Form or service agreement and the applicable delivery
        and refund terms. Customer-specific agreed provisions prevail over these general Terms in the event of a
        conflict, subject to mandatory law.
      </p>

      <h4>10.6 Modifications</h4>
      <p>
        We may update these Terms by providing notice to you. Continued use of our Services after such notice
        constitutes acceptance of the updated Terms.
      </p>

      <h4>10.7 Severability</h4>
      <p>If any provision of this Agreement is found unenforceable, the remainder will continue in effect.</p>

      <h4>10.8 Survival</h4>
      <p>Provisions that by their nature should survive termination will remain in effect after this Agreement ends.</p>

      <hr />

      <h3>DEFINITIONS</h3>
      <p>
        <strong>Customer Data</strong>: All data, content, and information provided by you or your end users through the
        Platform.
      </p>
      <p>
        <strong>Order Form</strong>: A signed agreement or online subscription specifying the Services, fees, and other
        terms.
      </p>
      <p>
        <strong>Outputs</strong>: Content generated by our AI models in response to your inputs.
      </p>
      <p>
        <strong>Platform</strong>: Palmate's AI-powered chatbot development platform and associated tools.
      </p>
      <p>
        <strong>Professional Services</strong>: Consulting, implementation, or other services provided by our team.
      </p>
      <p>
        <strong>Services</strong>: Collectively, the Platform, APIs, Professional Services, and any other services we
        provide.
      </p>

      <hr />

      <p>
        For questions about these Terms, please contact us at <a href="mailto:legal@palmate.ai">legal@palmate.ai</a>.
      </p>
      <p>
        <strong>Palamar Teknoloji Anonim Şirketi</strong>
        <br />
        Teknopark Istanbul, 1/10C Z42 Pendik/Istanbul
        <br />
        info@palmate.ai
      </p>
    </div>
  );
}

export function TermsOfUseTR() {
  return (
    <div className={styles.policy}>
      <h3>Hizmet Şartları</h3>
      <p>
        <strong>Son Güncelleme: 8 Ekim 2026</strong>
      </p>

      <p>
        Bu Kullanım Şartları (“Şartlar”), herhangi bir Sipariş Formu ve burada atıfla dâhil edilen diğer belgelerle
        birlikte (topluca “Sözleşme”), Palamar Teknoloji Anonim Şirketi (“Palmate”, “Şirket”, “biz” veya “bize”) ile Hizmetlerimizi kullanan
        tüzel kişi veya gerçek kişi (“Müşteri”, “siz” veya “sizin”) arasında bağlayıcı bir hukuki sözleşme oluşturur. Bu
        Şartlar, yapay zekâ destekli sohbet botu geliştirme hizmetlerimize ve platformumuza erişiminizi ve kullanımınızı
        düzenler. Hizmetlerimizi kullanarak veya bir Sipariş Formu imzalayarak bu Şartlara bağlı kalmayı kabul
        edersiniz.
      </p>

      <h3>1. HİZMETLER VE PLATFORM</h3>

      <h4>1.1 Platform Erişimi</h4>
      <ul>
        <li>Yapay zekâ destekli sohbet botu geliştirme platformumuz (“Platform”)</li>
        <li>İlişkili API’ler ve geliştirme araçları (“API’ler”)</li>
        <li>Herhangi bir Sipariş Formu veya İş Bildirimi’nde belirtilen profesyonel hizmetler</li>
      </ul>

      <h4>1.2 Hizmet Güncellemeleri</h4>
      <p>
        İşlevselliği maddi olarak azaltmamak kaydıyla Hizmetlerimizde değişiklik ve güncelleme yapma hakkını saklı
        tutarız. Maddi değişiklikleri e-posta veya platform bildirimi ile bildiririz.
      </p>

      <h4>1.3 Sizin Yükümlülükleriniz</h4>
      <ul>
        <li>Platformu yalnızca bu Sözleşmeye ve yürürlükteki hukuka uygun kullanmak</li>
        <li>Hesabınız altındaki tüm faaliyetlerden sorumlu olmak</li>
        <li>Platformu aktarmamak, yeniden satmamak veya yetkisiz üçüncü kişilere sunmamak</li>
        <li>Yetkisiz erişimi önlemek için uygun güvenlik önlemleri uygulamak</li>
        <li>Sohbet botlarınızın yürürlükteki hukuka uygunluğunu sağlamak</li>
        <li>Sohbet botlarınızla etkileşime giren son kullanıcılardan gerekli açık rızaları almak</li>
      </ul>

      <h4>1.4 Kabul Edilebilir Kullanım</h4>
      <ul>
        <li>Yürürlükteki kanun veya düzenlemeleri ihlal etmek</li>
        <li>Fikri mülkiyet haklarını ihlal etmek</li>
        <li>Zararlı, saldırgan veya yanıltıcı içerik üretmek</li>
        <li>Hileli veya aldatıcı uygulamalara girişmek</li>
        <li>Kötü amaçlı yazılım veya zararlı kod dağıtmak</li>
        <li>Platformun çalışmasına müdahale etmek</li>
      </ul>

      <h4>1.5 SMS ve Mobil Mesajlaşma Şartları</h4>
      <p>
        Palamar Teknoloji Anonim Şirketi tarafından işletilen Palmate; hesap doğrulama, tek kullanımlık şifre (OTP),
        kimlik doğrulama, güvenlik bildirimleri, hesap veya hizmet bildirimleri ve müşteri desteği için işlemsel SMS
        mesajları gönderebilir. Bu mesajlar hesap faaliyeti, kullanıcı talebi veya izin verilen başka bir işlemsel olay
        kapsamında gönderilir.
      </p>
      <p>
        Palmate ayrıca ürün veya hizmet duyuruları, teklifler, promosyonlar, etkinlik davetleri ve kampanya mesajları
        dahil pazarlama SMS'leri gönderebilir; ancak bunlar yalnızca alıcının ayrıca gerekli açık rızayı vermesi halinde
        gönderilir. Pazarlama SMS'lerine izin vermek isteğe bağlıdır ve satın alma ya da hizmet alma şartı değildir.
      </p>
      <ul>
        <li>Mesaj sıklığı hesap faaliyetlerine ve kullanıcının dahil olduğu mesajlaşma programına göre değişir.</li>
        <li>Mesaj ve veri ücretleri uygulanabilir.</li>
        <li><strong>SMS mesajlarından çıkmak için STOP yanıtını verin.</strong></li>
        <li><strong>Yardım için HELP yanıtını verin.</strong></li>
        <li>Destek için support@palmate.ai adresine ulaşabilirsiniz.</li>
        <li>Operatörler geciken veya teslim edilemeyen mesajlardan sorumlu değildir.</li>
      </ul>
      <p>
        Pazarlama mesajlarından çıkmak; gerekli olduğu durumlarda güvenlik, kimlik doğrulama veya hukuken izin verilen
        diğer işlemsel mesajların gönderilmesini engellemez. Cep telefonu numaralarını ve SMS opt-in verilerini nasıl
        işlediğimiz Gizlilik Politikamızda açıklanmaktadır. SMS izni, üçüncü tarafların veya bağlı kuruluşların kendi
        pazarlama ya da tanıtım amaçları için paylaşılmaz.
      </p>

      <h3>2. FİKRİ MÜLKİYET VE VERİ</h3>

      <h4>2.1 Sizin Verileriniz</h4>
      <p>
        Platformumuza sağladığınız tüm verilerin (“Müşteri Verisi”) mülkiyeti sizde kalır. Müşteri Verinizi yalnızca
        Hizmetleri sunmak ve toplulaştırılmış, kimliği giderilmiş analizler yoluyla Platformumuzu geliştirmek için
        işlememize yönelik sınırlı bir lisans verirsiniz.
      </p>

      <p>
        Kurumsal müşteriler için yukarıdaki lisans, müşterinin talimatları ve geçerli hizmet sözleşmesi ile Veri İşleme
        Eki (DPA) doğrultusunda kararlaştırılan hizmetlerin sunulmasıyla sınırlıdır. Palmate; konuşmalar, yüklenen
        belgeler ve bilgi tabanı içerikleri dahil kurumsal müşteri verilerini yapay zekâ modellerini eğitmek, ince ayar
        yapmak veya geliştirmek için kullanmaz. Bu kısıtlama, kurumsal müşteri verilerinin toplulaştırılmış veya
        kimlikten arındırılmış biçimleri için de geçerlidir.
      </p>

      <h4>2.2 Bizim Fikri Haklarımız</h4>
      <ul>
        <li>Platform ve ilişkili teknoloji</li>
        <li>Şirketimize ait özel yapay zekâ modelleri ve algoritmalar</li>
        <li>Geliştirdiğimiz her türlü iyileştirme veya geliştirme</li>
        <li>Toplulaştırılmış ve anonimleştirilmiş kullanım verileri</li>
      </ul>

      <h4>2.3 Üretilen İçerik</h4>
      <p>
        Yapay zekâ modellerimiz tarafından üretilen içerik (“Çıktılar”) sizin kullanımınıza sunulur. Ancak Çıktıların
        benzersiz, doğru veya hatalardan ya da önyargılardan ari olacağını garanti etmiyoruz.
      </p>

      <h3>3. YZ YÖNETİŞİMİ VE SINIRLAR</h3>

      <h4>3.1 YZ Teknolojisi Kabulü</h4>
      <ul>
        <li>Hizmetlerimiz yapay zekâ ve makine öğrenmesi teknolojileri kullanır</li>
        <li>YZ tarafından üretilen içerikte hata, önyargı veya yanlışlık bulunabilir</li>
        <li>Çıktılar kullanılmadan önce gözden geçirilmeli ve doğrulanmalıdır</li>
        <li>YZ sistemlerinin doğal sınırlamaları vardır ve beklenmedik sonuçlar üretebilir</li>
      </ul>

      <h4>3.2 İnsan Denetimi</h4>
      <p>
        YZ tarafından üretilen içerik üzerinde uygun insan denetimini sürdürmeyi ve kritik kararlar için yalnızca
        otomatik sistemlere güvenmemeyi kabul edersiniz.
      </p>

      <h4>3.3 Yasak YZ Kullanımları</h4>
      <ul>
        <li>Kredi değerliliği, istihdam veya sigorta hakkında karar vermek</li>
        <li>Aldatmak veya yanıltmak amacıyla içerik üretmek</li>
        <li>Uygun açıklama olmadan deepfake veya başka sentetik medya oluşturmak</li>
        <li>Açık rıza olmadan gözetim veya izleme yapmak</li>
      </ul>

      <h3>4. ÜCRETLER VE ÖDEME</h3>

      <h4>4.1 Abonelik Ücretleri</h4>
      <p>
        Ücretler, para birimi, varsa sabit paket ve kurulum bedelleri ile satın alınan hizmetin kapsamı, tarafların
        kabul ettiği Sipariş Formu veya hizmet sözleşmesinde belirtilir.
      </p>

      <h4>4.2 Kullanım Bazlı Ücretler</h4>
      <p>
        Kullanım ücretleri, içinde bulunulan ayın sonunda gerçekleşen kullanım üzerinden, sözleşmede belirlenen
        birim ücret ve koşullara göre hesaplanır.
      </p>

      <h4>4.3 Ödeme Şartları</h4>
      <p>Ödeme kullanım sonrasında ay sonunda yapılır. Faturalandırma, ödeme ve gecikmenin sonuçları bakımından taraflar arasındaki sözleşme hükümleri ve uygulanabilir mevzuat geçerlidir.</p>

      <h4>4.4 Vergiler</h4>
      <p>
        KDV'nin fiyata dahil olup olmadığı ilgili fiyat veya teklifte belirtilir. Toplam bedel ve uygulanabilir
        vergiler ödeme öncesinde açıklanır; uygulanacak bedeller kabul edilen Sipariş Formu veya hizmet sözleşmesine göre belirlenir.
      </p>

      <h4>4.5 İadeler</h4>
      <p>Kullanılmamış mesaj hakları için ücret iadesi yapılmaz. Faturalandırma veya tahsilat hataları ayrı olarak incelenir. Diğer iade talepleri sözleşme ve uygulanabilir mevzuat çerçevesinde değerlendirilir; emredici mevzuattan doğan haklar saklıdır.</p>

      <h3>5. GİZLİLİK</h3>
      <p>
        Taraflar, diğer tarafın gizli bilgilerini korumayı ve yalnızca bu Sözleşme ile ilgili amaçlarla kullanmayı kabul
        eder. Kamuya açık bilgiler veya bağımsız olarak geliştirilen bilgiler gizli bilgi sayılmaz.
      </p>

      <h3>6. GARANTİLER VE SORUMLULUK REDDİ</h3>

      <h4>6.1 Bizim Garantilerimiz</h4>
      <p>
        Hizmetlerimizin yayımlanmış teknik özelliklerimize ve sektör standartlarına esasen uygun şekilde çalışacağını
        garanti ederiz.
      </p>

      <h4>6.2 Sizin Garantileriniz</h4>
      <ul>
        <li>Bize sağladığınız tüm Müşteri Verilerini kullanma hakkına sahipsiniz</li>
        <li>Hizmetlerimizi kullanımınız yürürlükteki hukuka uygundur</li>
        <li>Son kullanıcılardan gerekli rızaları alacaksınız</li>
      </ul>

      <h4>6.3 SORUMLULUK REDDİ</h4>
      <p>
        AÇIKÇA BELİRTİLENLER HARİCİNDE, HİZMETLER “OLDUĞU GİBİ” SAĞLANIR VE HER TÜRLÜ ZIMNİ GARANTİ REDDEDİLİR. TİCARİ
        ELVERİŞLİLİK, BELİRLİ BİR AMACA UYGUNLUK VE İHLAL ETMEME DÂHİL.
      </p>

      <h4>6.4 YZ SINIRLARI SORUMLULUK REDDİ</h4>
      <p>
        YZ TARAFINDAN ÜRETİLEN İÇERİK HATA, ÖNYARGI VEYA YANLIŞLIKLAR İÇEREBİLİR. BU İÇERİĞİN DOĞRULUĞU, EKSİKSİZLİĞİ VE
        GÜVENİLİRLİĞİNE DAİR GARANTİ VERMEYİZ. TÜM ÇIKTILARI GÖZDEN GEÇİRME VE DOĞRULAMA SORUMLULUĞU SİZE AİTTİR.
      </p>

      <h3>7. TAZMİN</h3>

      <h4>7.1 Bizim Tazmin Yükümlülüğümüz</h4>
      <p>
        Hızlı bildiriminiz ve iş birliğiniz şartıyla, Hizmetlerimizin geçerli fikri mülkiyet haklarını ihlal ettiğine
        dair üçüncü taraf iddialarına karşı sizi savunacağız.
      </p>

      <h4>7.2 Sizin Tazmin Yükümlülüğünüz</h4>
      <ul>
        <li>Bu Sözleşmenin ihlali</li>
        <li>Müşteri Veriniz veya bunun kullanımı</li>
        <li>Sohbet botlarınız veya son kullanıcı etkileşimleri</li>
        <li>Yürürlükteki hukukun ihlali</li>
      </ul>

      <h3>8. SORUMLULUK SINIRI</h3>

      <h4>8.1 Sınır</h4>
      <p>
        Tarafların bu Sözleşme kapsamındaki tüm talepler için toplam sorumluluğu, talebi önceleyen 12 ay içinde
        ödediğiniz ücretleri aşmaz.
      </p>

      <h4>8.2 Hariç Zararlar</h4>
      <p>
        Taraflardan hiçbiri dolaylı, sonuçsal, cezai veya özel zararlardan, kâr veya iş fırsatı kaybından sorumlu
        değildir.
      </p>

      <h4>8.3 İstisnalar</h4>
      <p>Bu sınırlamalar tazmin yükümlülükleri, gizlilik ihlalleri veya ağır ihmal için uygulanmaz.</p>

      <h3>9. SÜRE VE FESİH</h3>

      <h4>9.1 Süre</h4>
      <p>
        Hizmet, aktivasyon tamamlandıktan sonra kullanıcının sisteme erişmesiyle başlar. Hizmet süresi, taahhüt,
        bildirim ve sona erme süreleri kabul edilen Sipariş Formu veya hizmet sözleşmesinde belirtildiği şekilde uygulanır.
      </p>

      <h4>9.2 Fesih Hakları</h4>
      <p>Bildirim ve ihlali giderme süreleri dahil iptal ve fesih hakları, taraflar arasındaki sözleşmeye ve uygulanabilir mevzuata tabidir. İptal talebi sözleşmeden doğan ödeme yükümlülüklerini kendiliğinden ortadan kaldırmaz.</p>

      <h4>9.3 Feshin Sonuçları</h4>
      <ul>
        <li>Platforma erişiminiz sona erer</li>
        <li>Tüm ödenmemiş ücretleri ödersiniz</li>
        <li>Verilerinizi dışa aktarma için makul bir süre saklarız</li>
        <li>Gizlilik yükümlülükleri yürürlükte kalır</li>
      </ul>

      <h3>10. GENEL HÜKÜMLER</h3>

      <h4>10.1 Uygulanacak Hukuk</h4>
      <p>Bu Sözleşme, ihtilaf kuralları hariç, Türkiye Cumhuriyeti hukukuna tabidir.</p>

      <h4>10.2 Uyuşmazlık Çözümü</h4>
      <p>
        Fikri mülkiyet haklarına ilişkin talepler hariç olmak üzere uyuşmazlıklar İstanbul'da bağlayıcı tahkim yoluyla
        çözülecektir.
      </p>

      <h4>10.3 Mücbir Sebep</h4>
      <p>Taraflar, makul kontrolleri dışındaki sebeplerden kaynaklanan gecikme veya ifa edememeden sorumlu tutulmaz.</p>

      <h4>10.4 Devretme</h4>
      <p>
        Bu Sözleşmeyi bizim yazılı onayımız olmadan devredemezsiniz. Biz, birleşme, iktisap veya varlık satışı
        kapsamında devredebiliriz.
      </p>

      <h4>10.5 Bütün Sözleşme</h4>
      <p>
        Bu Şartlar, kabul edilen Sipariş Formu veya hizmet sözleşmesi ve uygulanabilir teslimat ve iade koşullarıyla
        birlikte değerlendirilir. Farklılık olması hâlinde, emredici mevzuat saklı kalmak üzere müşteriye özel olarak
        kabul edilmiş hükümler bu genel Şartlara göre önceliklidir.
      </p>

      <h4>10.6 Değişiklikler</h4>
      <p>
        Size bildirimde bulunarak bu Şartları güncelleyebiliriz. Bildirim sonrası Hizmetleri kullanmaya devam etmeniz
        güncellenmiş Şartları kabul ettiğiniz anlamına gelir.
      </p>

      <h4>10.7 Bölünebilirlik</h4>
      <p>Herhangi bir hükmün uygulanamaz bulunması hâlinde kalan hükümler yürürlükte kalır.</p>

      <h4>10.8 Yürürlükte Kalma</h4>
      <p>
        Niteliği gereği sona ermeden sonra da uygulanması gereken hükümler, Sözleşme’nin bitiminden sonra da yürürlükte
        kalır.
      </p>

      <hr />

      <h3>TANIMLAR</h3>
      <p>
        <strong>Müşteri Verisi</strong>: Platform üzerinden sizin veya son kullanıcılarınız tarafından sağlanan tüm
        veri, içerik ve bilgiler.
      </p>
      <p>
        <strong>Sipariş Formu</strong>: Hizmetleri, ücretleri ve diğer şartları belirleyen imzalı anlaşma veya çevrimiçi
        abonelik.
      </p>
      <p>
        <strong>Çıktılar</strong>: Girdilerinize yanıt olarak yapay zekâ modellerimiz tarafından üretilen içerik.
      </p>
      <p>
        <strong>Platform</strong>: Palmate’in yapay zekâ destekli sohbet botu geliştirme platformu ve ilişkili araçlar.
      </p>
      <p>
        <strong>Profesyonel Hizmetler</strong>: Ekibimiz tarafından sunulan danışmanlık, uygulama ve benzeri hizmetler.
      </p>
      <p>
        <strong>Hizmetler</strong>: Platform, API’ler, Profesyonel Hizmetler ve sağladığımız diğer tüm hizmetler.
      </p>

      <hr />

      <p>
        Bu Şartlara ilişkin sorular için bize <a href="mailto:legal@palmate.ai">legal@palmate.ai</a> adresinden
        ulaşabilirsiniz.
      </p>
      <p>
        <strong>Palamar Teknoloji Anonim Şirketi</strong>
        <br />
        Teknopark Istanbul, 1/10C Z42 Pendik/Istanbul
        <br />
        info@palmate.ai
      </p>
    </div>
  );
}

